import platformConfigService from '../services/platformConfigService';

/**
 * Utilidad para migrar datos de localStorage a Firebase
 * Solo se ejecuta una vez por usuario para migrar datos existentes
 */

export const migrateLocalStorageToFirebase = async (userId: string): Promise<void> => {
  try {
    console.log('🔄 Iniciando migración de localStorage a Firebase...');

    // Verificar si ya hay datos en Firebase
    const existingConfigs = await platformConfigService.getUserConfigs();
    if (existingConfigs && (existingConfigs.ecommerce?.length > 0 || existingConfigs.socialMedia?.length > 0)) {
      console.log('✅ Ya existen configuraciones en Firebase, saltando migración');
      return;
    }

    let hasDataToMigrate = false;
    const migratedData: any = {};

    // Migrar configuraciones de e-commerce
    const ecommerceKey = `ecommerce_platforms_${userId}`;
    const ecommerceData = localStorage.getItem(ecommerceKey);
    if (ecommerceData) {
      try {
        const parsedEcommerce = JSON.parse(ecommerceData);
        migratedData.ecommerce = parsedEcommerce.map((platform: any) => ({
          id: platform.id,
          name: platform.name,
          enabled: platform.enabled,
          description: platform.description,
          icon: platform.icon,
          connected: platform.connected,
          apiKey: platform.apiKey,
          apiSecret: platform.apiSecret,
          lastUpdated: new Date()
        }));
        hasDataToMigrate = true;
        console.log('📦 Datos de e-commerce encontrados para migrar:', parsedEcommerce.length, 'plataformas');
      } catch (error) {
        console.warn('⚠️ Error parseando datos de e-commerce:', error);
      }
    }

    // Migrar configuraciones de redes sociales
    const socialKey = `social_media_platforms_${userId}`;
    const socialData = localStorage.getItem(socialKey);
    if (socialData) {
      try {
        const parsedSocial = JSON.parse(socialData);
        migratedData.socialMedia = parsedSocial.map((platform: any) => ({
          id: platform.id,
          name: platform.name,
          enabled: platform.enabled,
          description: platform.description,
          icon: platform.icon,
          connected: platform.connected,
          accessToken: platform.accessToken,
          refreshToken: platform.refreshToken,
          lastUpdated: new Date()
        }));
        hasDataToMigrate = true;
        console.log('📱 Datos de redes sociales encontrados para migrar:', parsedSocial.length, 'plataformas');
      } catch (error) {
        console.warn('⚠️ Error parseando datos de redes sociales:', error);
      }
    }

    // Si hay datos para migrar, guardarlos en Firebase
    if (hasDataToMigrate) {
      await platformConfigService.saveUserConfigs(migratedData);
      console.log('✅ Migración completada exitosamente');

      // Opcional: Limpiar localStorage después de la migración exitosa
      if (ecommerceData) {
        localStorage.removeItem(ecommerceKey);
        console.log('🗑️ Datos de e-commerce eliminados de localStorage');
      }
      if (socialData) {
        localStorage.removeItem(socialKey);
        console.log('🗑️ Datos de redes sociales eliminados de localStorage');
      }

      // Marcar que la migración se completó
      localStorage.setItem(`migration_completed_${userId}`, 'true');
      console.log('🏁 Migración marcada como completada');
    } else {
      console.log('ℹ️ No hay datos para migrar');
    }

  } catch (error) {
    console.error('❌ Error durante la migración:', error);
    throw error;
  }
};

/**
 * Verificar si la migración ya se completó para un usuario
 */
export const isMigrationCompleted = (userId: string): boolean => {
  return localStorage.getItem(`migration_completed_${userId}`) === 'true';
};

/**
 * Ejecutar migración si es necesaria
 */
export const runMigrationIfNeeded = async (userId: string): Promise<void> => {
  if (!isMigrationCompleted(userId)) {
    await migrateLocalStorageToFirebase(userId);
  }
};
